import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, map } from 'rxjs';

import { LoadingStatus } from '@mp/shared/domain';

import { CreateFavoriteListDto, FavoritesList, UpdateFavoriteListDto } from '../models';

import { FavoritesActions } from './favorites.actions';
import { selectFavoritesLists, selectFavoritesListsStatus } from './favorites.selectors';

@Injectable()
export class FavoritesFacade {
  private readonly favoritesStatus$: Observable<LoadingStatus> = this.store$.select(selectFavoritesListsStatus);

  readonly favoritesListsLoading$: Observable<boolean> = this.favoritesStatus$.pipe(
    map((status) => status === LoadingStatus.Loading),
  );

  readonly favoritesLists$: Observable<FavoritesList[]> = this.store$.select(selectFavoritesLists);

  readonly favoriteListCreateSuccess$: Observable<FavoritesList> = this.actions$.pipe(
    ofType(FavoritesActions.createFavoritesListSuccess),
    map(({ createdList }) => createdList),
  );

  readonly favoriteListCreateError$: Observable<string> = this.actions$.pipe(
    ofType(FavoritesActions.createFavoritesListError),
    map(({ listName }) => listName),
  );

  readonly favoriteListDeleteSuccess$: Observable<string> = this.actions$.pipe(
    ofType(FavoritesActions.deleteFavoritesListSuccess),
    map(({ listId }) => listId),
  );

  constructor(
    private readonly store$: Store,
    private readonly actions$: Actions,
  ) {}

  fetchFavoritesLists(): void {
    this.store$.dispatch(FavoritesActions.fetchFavoritesLists());
  }

  createFavoritesList(createDto: CreateFavoriteListDto): void {
    this.store$.dispatch(FavoritesActions.createFavoritesList({ createDto }));
  }

  updateFavoritesList(listId: string, updateDto: UpdateFavoriteListDto): void {
    this.store$.dispatch(FavoritesActions.updateFavoritesList({ listId, updateDto }));
  }

  updateFavoritesListArticleCount(listId: string, articleCount: number): void {
    this.store$.dispatch(FavoritesActions.updateFavoritesListArticleCount({ listId, articleCount }));
  }

  deleteFavoritesList(listId: string): void {
    this.store$.dispatch(FavoritesActions.deleteFavoritesList({ listId }));
  }

  exportFavoritesList(listId: string, exportType: string): void {
    this.store$.dispatch(FavoritesActions.exportFavoritesList({ listId, exportType }));
  }
}
